import classNames from 'classnames';
import { PropsWithChildren, forwardRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { StyledTooltipContent } from './StyledTooltip';
import { Placement } from './Tooltip';

interface TooltipContentProps {
  /**
   * Id the trigger's `aria-describedby` points at. The content is portalled
   * outside the trigger, so this attribute is the only thing associating the
   * two (ISSUE-063).
   */
  id?: string;
  isActive: boolean;
  placement: Placement | null;
  maxWidth?: number;
}

const TooltipContent = forwardRef<
  HTMLDivElement,
  PropsWithChildren<TooltipContentProps>
>(({ id, isActive, placement, maxWidth = 0, children }, ref) => {
  // If the maxWidth is set, we need to wrap the content and break the text.
  const contentWrapStyle = useMemo(() => {
    if (maxWidth > 0)
      return {
        width: `${maxWidth}px`,
        maxWidth: `${maxWidth}px`,
        whiteSpace: 'pre-wrap' as const,
        wordBreak: 'break-all' as const,
      };
    else return {};
  }, [maxWidth]);

  return createPortal(
    <StyledTooltipContent
      ref={ref}
      id={id}
      // Purely visual. The accessible description is carried by
      // `StyledTooltipDescription` inside the trigger's container, so exposing
      // this portalled copy too would announce the same text twice.
      aria-hidden="true"
      className={classNames(
        'sup-tooltip',
        isActive && 'sup-tooltip-active',
        !placement && 'sup-tooltip-hidden'
      )}
    >
      <div className="sup-tooltip-content" style={contentWrapStyle}>
        {children}
      </div>
    </StyledTooltipContent>,
    document.getElementById('tooltip-portal') as HTMLElement
  );
});

export default TooltipContent;
